import { useState } from "react";
import { X, Download } from "lucide-react";
import { base44 } from "@/api/base44Client";
import { shouldTrack } from "@/lib/pageTracking";

const PDF_URL = "https://media.base44.com/files/public/6a316287a1be7d9945ac7d10/ca82e5180_Truestays_Essentials_Checklist_Interactive_2.pdf";

export default function ChecklistDownloadModal({ onClose }) {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const validate = () => {
    const newErrors = {};
    if (!fullName.trim()) newErrors.fullName = "Name is required";
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) newErrors.email = "Valid email is required";
    if (!consent) newErrors.consent = "Please confirm consent to continue";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);

    shouldTrack().then((track) => {
      if (!track) return;
      base44.entities.ResourceEvent.create({
        page: "resource:short-let-setup-checklist",
        event_type: "click",
        element_label: "pdf_download",
        user_email: "admin@truestays.co.uk",
        session_id: `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      });
    });

    try {
      await base44.functions.invoke("sendLeadEmail", {
        subject: `Checklist PDF download – ${fullName}`,
        body: `Name: ${fullName}\nEmail: ${email}\nMarketing consent: ${consent ? "Yes" : "No"}`,
      });
    } catch (_) {}

    // Email the checklist PDF to the user
    try {
      await base44.integrations.Core.SendEmail({
        to: email,
        from_name: "Truestays",
        subject: "Your Truestays Essentials Checklist",
        body: `Hi ${fullName},\n\nThanks for downloading the Truestays Essentials Checklist! You can access your copy here:\n\n${PDF_URL}\n\nThis checklist covers everything your property needs before welcoming your first guests — from certificates and safety to kitchen essentials and highly recommended extras.\n\nIf you'd like a free income estimate or have any questions, just reply to this email or visit truestays.co.uk.\n\nBest,\nThe Truestays Team`,
      });
    } catch (_) {}

    window.open(PDF_URL, "_blank");
    setSubmitting(false);
    onClose();
  };

  return (
    <div
      style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ width: "100%", maxWidth: 440, background: "#fff", borderRadius: 16, padding: "32px 28px", position: "relative", boxShadow: "0 24px 80px rgba(0,0,0,0.3)" }}>
        <button onClick={onClose} style={{ position: "absolute", top: 16, right: 16, background: "#F5F5F5", border: "none", borderRadius: "50%", width: 32, height: 32, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <X size={16} color="#888" />
        </button>

        <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "#4A6DC7", marginBottom: 6, marginTop: 0 }}>Free download</p>
        <h3 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 20, color: "#1A1A1A", marginBottom: 6, marginTop: 0 }}>Get the PDF checklist</h3>
        <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#888", marginTop: 0, marginBottom: 20, lineHeight: 1.55 }}>
          Enter your details below and we'll unlock the download instantly.
        </p>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: "0.07em", textTransform: "uppercase", color: "#888", display: "block", marginBottom: 6 }}>Name *</label>
            <input
              style={{ border: `1.5px solid ${errors.fullName ? "hsl(var(--primary))" : "#E5E5E5"}`, borderRadius: 10, padding: "13px 16px", fontFamily: "'DM Sans', sans-serif", fontSize: 15, color: "#1A1A1A", background: "#fff", width: "100%", boxSizing: "border-box", outline: "none" }}
              value={fullName}
              onChange={e => { setFullName(e.target.value); if (errors.fullName) setErrors(p => ({ ...p, fullName: undefined })); }}
              placeholder="Your name"
            />
            {errors.fullName && <p style={{ color: "#4A6DC7", fontSize: 11, marginTop: 4, fontFamily: "'DM Sans', sans-serif" }}>{errors.fullName}</p>}
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: "0.07em", textTransform: "uppercase", color: "#888", display: "block", marginBottom: 6 }}>Email *</label>
            <input
              type="email"
              style={{ border: `1.5px solid ${errors.email ? "hsl(var(--primary))" : "#E5E5E5"}`, borderRadius: 10, padding: "13px 16px", fontFamily: "'DM Sans', sans-serif", fontSize: 15, color: "#1A1A1A", background: "#fff", width: "100%", boxSizing: "border-box", outline: "none" }}
              value={email}
              onChange={e => { setEmail(e.target.value); if (errors.email) setErrors(p => ({ ...p, email: undefined })); }}
              placeholder="Email address"
            />
            {errors.email && <p style={{ color: "#4A6DC7", fontSize: 11, marginTop: 4, fontFamily: "'DM Sans', sans-serif" }}>{errors.email}</p>}
          </div>

          <div style={{ marginBottom: 20, display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer" }} onClick={() => { setConsent(v => !v); if (errors.consent) setErrors(p => ({ ...p, consent: undefined })); }}>
            <div style={{
              width: 18, height: 18, borderRadius: 4, flexShrink: 0, marginTop: 1,
              border: "1.5px solid " + (errors.consent ? "hsl(var(--primary))" : (consent ? "hsl(var(--primary))" : "#CCCCCC")),
              background: consent ? "hsl(var(--primary))" : "#fff",
              display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.15s",
            }}>
              {consent && (
                <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                  <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </div>
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: "#4A4A4A", lineHeight: 1.5, margin: 0, userSelect: "none" }}>
              I agree to receive marketing emails from Truestays. Required to download the checklist. You can unsubscribe at any time. See our{" "}
              <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" style={{ color: "#4A6DC7" }}>Privacy Policy</a>.
            </p>
          </div>
          {errors.consent && <p style={{ color: "#4A6DC7", fontSize: 11, marginTop: -12, marginBottom: 16, fontFamily: "'DM Sans', sans-serif" }}>{errors.consent}</p>}

          <button
            type="submit"
            disabled={submitting}
            style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, background: "hsl(var(--primary))", color: "#fff", border: "none", borderRadius: 100, padding: "14px", fontFamily: "'DM Sans', sans-serif", fontSize: 15, fontWeight: 600, width: "100%", cursor: "pointer" }}
          >
            <Download size={15} /> {submitting ? "Preparing…" : "Download PDF"}
          </button>
        </form>
      </div>
    </div>
  );
}